import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { serverFilterId, timeRangeFilterId } from '../../filters';

const title = t`Server CPU Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['avgCpu', 'statusCpuUtil'],
    },
};

export function useChartConfig() {
    const color = useMetricColor('serverCpu');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Utilization (%)`,
                    },
                    ceiling: 100,
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'statusCpuUtil',
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.PERCENT,
                    forceDecimals: true,
                    units: '%',
                    chartType: 'spline',
                    key: 'avgCpu',
                    label: t`Average CPU`,
                    color,
                    multiplier: 1,
                },
            ],
        }),
        [color]
    );
}

function ServerCpuUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig();
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

ServerCpuUtilizationPanel.propTypes = panelPropTypes;

ServerCpuUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'ServerCpuUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.SERVER,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`CPU utilization trend for a selected server`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, serverFilterId],
        optional: [],
    },
})(ServerCpuUtilizationPanel);
